import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { N8nChat } from './N8nChat';
import { GenericButton } from '../../../ui-components/Button/GenericButton';

// Placeholder data for chat history
const PLACEHOLDER_CHAT_HISTORY = [
  { id: 1, date: '2026-01-25 14:32', summary: 'Cloud connectivity quotation - AWS Direct Connect', status: 'completed' },
  { id: 2, date: '2026-01-24 10:15', summary: 'Enterprise Internet 10Gbps - Mexico City', status: 'completed' },
  { id: 3, date: '2026-01-23 16:45', summary: 'DCI between KIO MEX2 and EQX MX1', status: 'pending' },
  { id: 4, date: '2026-01-22 09:00', summary: 'Port provisioning - 100G Dedicated', status: 'completed' },
];

// Placeholder data for quotation PDFs
const PLACEHOLDER_QUOTATIONS = [
  { id: 'QT-2026-0125', date: '2026-01-25', customer: 'Acme Corp', amount: '$12,500/mo', file: 'quotation-0125.pdf' },
  { id: 'QT-2026-0122', date: '2026-01-22', customer: 'TechStart Inc', amount: '$8,200/mo', file: 'quotation-0122.pdf' },
  { id: 'QT-2026-0118', date: '2026-01-18', customer: 'Global Finance', amount: '$45,000/mo', file: 'quotation-0118.pdf' },
  { id: 'QT-2026-0115', date: '2026-01-15', customer: 'Media Networks', amount: '$22,750/mo', file: 'quotation-0115.pdf' },
];

type TabType = 'chat' | 'history' | 'quotations';

export default function AIQuotationPage() {
  const [activeTab, setActiveTab] = useState<TabType>('chat');
  const [accessToken, setAccessToken] = useState<string>('');
  
  // Get Auth0 user info and token
  const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
  
  // Get webhook URL from environment variable
  const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_URL || '';

  // Fetch access token when authenticated
  useEffect(() => {
    const fetchToken = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          setAccessToken(token);
        } catch (error) {
          console.error('[AIQuotation] Error getting access token:', error);
        }
      }
    };
    fetchToken();
  }, [isAuthenticated, getAccessTokenSilently]);

  const handleDownloadQuotation = (quotationId: string) => {
    // Placeholder - will implement actual download logic
    console.log('[AIQuotation] Download quotation:', quotationId);
    alert(`Download feature coming soon for ${quotationId}`);
  };

  const handleLoadChat = (chatId: number) => {
    // Placeholder - will implement chat history loading
    console.log('[AIQuotation] Load chat history:', chatId);
    alert(`Chat history loading coming soon for session #${chatId}`);
  };

  return (
    <div className="p-4 h-full overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-semantic-border-primary mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('chat')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'chat'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-semantic-text-secondary hover:text-semantic-text-primary hover:border-semantic-border-secondary'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Chat
            </span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'history'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-semantic-text-secondary hover:text-semantic-text-primary hover:border-semantic-border-secondary'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Chat History
            </span>
          </button>
          <button
            onClick={() => setActiveTab('quotations')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'quotations'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-semantic-text-secondary hover:text-semantic-text-primary hover:border-semantic-border-secondary'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Quotations (PDF)
            </span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-semantic-bg-primary border border-semantic-border-primary rounded-lg">
        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="p-4">
            {webhookUrl && accessToken ? (
              <div className="h-[calc(100vh-280px)] min-h-[400px] border border-semantic-border-secondary rounded-lg overflow-hidden">
                <N8nChat 
                  webhookUrl={webhookUrl}
                  userId={user?.sub}
                  userEmail={user?.email}
                  userToken={accessToken}
                />
              </div>
            ) : webhookUrl && !accessToken ? (
              <div className="h-[calc(100vh-280px)] min-h-[400px] flex items-center justify-center bg-semantic-bg-secondary rounded-lg">
                <div className="text-center">
                  <div className="animate-spin w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-semantic-text-secondary text-sm">Loading authentication...</p>
                </div>
              </div>
            ) : (
              <div className="h-[calc(100vh-280px)] min-h-[400px] flex items-center justify-center bg-semantic-bg-secondary rounded-lg">
                <div className="text-center">
                  <svg className="w-16 h-16 text-semantic-text-disabled mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-semantic-text-secondary font-medium">Chat not configured</p>
                  <p className="text-semantic-text-disabled text-sm mt-1">
                    Please set REACT_APP_N8N_WEBHOOK_URL environment variable
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chat History Tab */}
        {activeTab === 'history' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-semantic-text-primary">
                Previous Conversations
              </h3>
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                Placeholder - Coming Soon
              </span>
            </div>
            
            <div className="space-y-3">
              {PLACEHOLDER_CHAT_HISTORY.map((chat) => (
                <div 
                  key={chat.id}
                  className="flex items-center justify-between p-4 bg-semantic-bg-secondary rounded-lg border border-semantic-border-secondary hover:border-semantic-border-primary transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-semantic-text-primary">
                        {chat.summary}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        chat.status === 'completed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {chat.status}
                      </span>
                    </div>
                    <p className="text-xs text-semantic-text-secondary mt-1">
                      {chat.date}
                    </p>
                  </div>
                  <GenericButton
                    label="Load"
                    variant="outlined"
                    size="sm"
                    onClick={() => handleLoadChat(chat.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quotations PDF Tab */}
        {activeTab === 'quotations' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-semantic-text-primary">
                Generated Quotations
              </h3>
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                Placeholder - Coming Soon
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-semantic-border-secondary">
                    <th className="text-left py-3 px-4 font-medium text-semantic-text-secondary">Quote ID</th>
                    <th className="text-left py-3 px-4 font-medium text-semantic-text-secondary">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-semantic-text-secondary">Customer</th>
                    <th className="text-right py-3 px-4 font-medium text-semantic-text-secondary">Amount</th>
                    <th className="text-center py-3 px-4 font-medium text-semantic-text-secondary">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {PLACEHOLDER_QUOTATIONS.map((quote) => (
                    <tr 
                      key={quote.id}
                      className="border-b border-semantic-border-secondary hover:bg-semantic-bg-secondary transition-colors"
                    >
                      <td className="py-3 px-4 font-mono text-semantic-text-primary">
                        {quote.id}
                      </td>
                      <td className="py-3 px-4 text-semantic-text-secondary">
                        {quote.date}
                      </td>
                      <td className="py-3 px-4 text-semantic-text-primary">
                        {quote.customer}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-semantic-text-primary">
                        {quote.amount}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleDownloadQuotation(quote.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

